import type { NostrEvent } from '@nostrify/nostrify';

/**
 * BCHNostr business verification protocol (port of bchnostr.com's businessVerify.ts).
 *
 * Verification is a self-service, on-chain flow interoperable with BCHNostr:
 *
 *  1. The business pays the fee (default $10 USD, in BCH) to PLATFORM_FEE_ADDRESS.
 *  2. The business publishes a *claim* event (kind 30078, d = CLAIM_D) containing
 *     the payment txid — signed by the BUSINESS pubkey.
 *  3. A trusted issuer (GRANT_ISSUERS) reviews the claim and publishes a *grant*
 *     event (kind 30078, d = GRANT_D_PREFIX + businessPubkey), valid for 365 days.
 *  4. Clients read the newest grant per business to show the verified badge.
 *
 * These kinds/tags/addresses match BCHNostr exactly so verifications made here
 * are recognised there and vice-versa.
 */

/** Kind used for all verification events (application-specific data). */
export const BUSINESS_VERIFY_KIND = 30078;

/** `d` tag for a business's verification payment claim. */
export const BUSINESS_VERIFY_CLAIM_D = 'bchnostr/business-verify';
/** `t` marker tag on claim events. */
export const BUSINESS_VERIFY_CLAIM_TAG = 'bch-business-verify-claim';

/** `d` tag prefix for an issuer's verification grant (grant per business pubkey). */
export const BUSINESS_VERIFY_GRANT_D_PREFIX = 'bchnostr/business-verify-grant/';
/** `t` marker tag on grant events. */
export const BUSINESS_VERIFY_GRANT_TAG = 'bch-business-verify';

/** `d` tag for the issuer-published fee configuration event. */
export const BUSINESS_VERIFY_CONFIG_D = 'bchnostr/business-verify-config';

/** Default verification fee in USD when no config event is present. */
export const DEFAULT_BUSINESS_VERIFY_FEE_USD = 10;

/** Verification validity period. */
export const BUSINESS_VERIFY_PERIOD_DAYS = 365;
export const BUSINESS_VERIFY_PERIOD_SECS = BUSINESS_VERIFY_PERIOD_DAYS * 86400;

/** BCH address that receives the verification fee. */
export const PLATFORM_FEE_ADDRESS =
  'bitcoincash:qzfczm3kc9j8aw0pjm5azym8s8xg07ssru5yr5qepj';

/** Pubkeys trusted to issue verification grants (BCHNostr operator + grant key). */
export const VERIFY_GRANT_ISSUERS: string[] = [
  'df5bcaba9e74cc1764c9773ae160299dde5acf79416e2c96758dba225e6707cc',
  'cc71a57c6417efdbc9e4966472f1199911007e96b09420763a9bd1687e300862',
];

const TXID_RE = /^[0-9a-f]{64}$/;
const HEX64 = /^[0-9a-f]{64}$/;

/** Whether a pubkey is a trusted grant issuer. */
export function isGrantIssuer(pubkey: string): boolean {
  return VERIFY_GRANT_ISSUERS.includes(pubkey.trim().toLowerCase());
}

/** Convert a USD fee into satoshis at the given BCH price. */
export function verifyFeeSats(feeUsd: number, bchPriceUsd: number | null): number {
  if (!bchPriceUsd || bchPriceUsd <= 0) return 0;
  return Math.round((feeUsd / bchPriceUsd) * 1e8);
}

/** The `d` tag for a grant targeting a specific business. */
export function verifyGrantDTag(businessPubkey: string): string {
  return `${BUSINESS_VERIFY_GRANT_D_PREFIX}${businessPubkey.toLowerCase()}`;
}

/** A parsed verification claim (published by the business). */
export interface VerifyClaim {
  pubkey: string;
  txid: string;
  amountSats: number;
  claimedAt: number;
  event: NostrEvent;
}

/** A parsed verification grant (published by an issuer). */
export interface VerificationGrant {
  businessPubkey: string;
  issuedBy: string;
  startAt: number;
  expiresAt: number;
  txid: string;
  revoked: boolean;
  issuedAt: number;
  event: NostrEvent;
}

/** Verification status for a business. */
export type VerificationStatus = 'VERIFIED' | 'PENDING' | 'EXPIRED' | 'UNVERIFIED';

export interface VerificationState {
  status: VerificationStatus;
  grant: VerificationGrant | null;
  claim: VerifyClaim | null;
  startAt: number;
  expiresAt: number;
  daysUntilExpiry: number;
}

/** Build a claim event (to be signed & published by the business). */
export function buildVerifyClaimEvent(txid: string, amountSats: number): {
  kind: number;
  tags: string[][];
  content: string;
} {
  const now = Math.floor(Date.now() / 1000);
  const cleanTxid = TXID_RE.test((txid || '').toLowerCase()) ? txid.toLowerCase() : '';
  return {
    kind: BUSINESS_VERIFY_KIND,
    tags: [
      ['d', BUSINESS_VERIFY_CLAIM_D],
      ['t', BUSINESS_VERIFY_CLAIM_TAG],
      ['alt', 'BCHNostr business verification payment'],
      ...(cleanTxid ? [['fee', cleanTxid, String(amountSats), PLATFORM_FEE_ADDRESS]] : []),
    ],
    content: JSON.stringify({ txid: cleanTxid, amountSats, claimedAt: now }),
  };
}

/** Parse a claim event. Returns null if it isn't a valid claim. */
export function parseVerifyClaim(event: NostrEvent): VerifyClaim | null {
  if (event.kind !== BUSINESS_VERIFY_KIND) return null;
  const d = event.tags.find(([n]) => n === 'd')?.[1];
  if (d !== BUSINESS_VERIFY_CLAIM_D) return null;
  if (!event.tags.some(([n, v]) => n === 't' && v === BUSINESS_VERIFY_CLAIM_TAG)) return null;

  let txid = '';
  let amountSats = 0;
  let claimedAt = event.created_at;
  try {
    const c = JSON.parse(event.content);
    if (typeof c.txid === 'string') txid = c.txid.toLowerCase();
    if (typeof c.amountSats === 'number') amountSats = c.amountSats;
    if (typeof c.claimedAt === 'number') claimedAt = c.claimedAt;
  } catch {
    // ignore
  }
  if (!TXID_RE.test(txid)) {
    const feeTag = event.tags.find(([n]) => n === 'fee');
    if (feeTag && TXID_RE.test((feeTag[1] || '').toLowerCase())) txid = feeTag[1].toLowerCase();
  }
  return { pubkey: event.pubkey, txid, amountSats, claimedAt, event };
}

/** Build a grant event (signed & published by an issuer). */
export function buildVerifyGrantEvent(opts: {
  businessPubkey: string;
  issuedBy: string;
  txid?: string;
  startAt?: number;
  expiresAt?: number;
  revoked?: boolean;
}): { kind: number; tags: string[][]; content: string } {
  const now = Math.floor(Date.now() / 1000);
  const startAt = opts.startAt ?? now;
  const expiresAt = opts.expiresAt ?? startAt + BUSINESS_VERIFY_PERIOD_SECS;
  const txid = TXID_RE.test((opts.txid || '').toLowerCase()) ? opts.txid!.toLowerCase() : '';
  const businessPubkey = opts.businessPubkey.toLowerCase();
  return {
    kind: BUSINESS_VERIFY_KIND,
    tags: [
      ['d', verifyGrantDTag(businessPubkey)],
      ['t', BUSINESS_VERIFY_GRANT_TAG],
      ['p', businessPubkey],
      ['alt', opts.revoked ? 'BCHNostr business verification revoked' : 'BCHNostr verified business'],
    ],
    content: JSON.stringify({
      businessPubkey,
      issuedBy: opts.issuedBy.toLowerCase(),
      startAt,
      expiresAt,
      txid,
      revoked: !!opts.revoked,
      issuedAt: now,
    }),
  };
}

/**
 * Parse a grant event. Only events authored by a trusted issuer are accepted;
 * this is the trust boundary for the verified badge.
 */
export function parseVerificationGrant(event: NostrEvent): VerificationGrant | null {
  if (event.kind !== BUSINESS_VERIFY_KIND) return null;
  if (!isGrantIssuer(event.pubkey)) return null;

  const d = event.tags.find(([n]) => n === 'd')?.[1] || '';
  if (!d.startsWith(BUSINESS_VERIFY_GRANT_D_PREFIX)) return null;

  let businessPubkey = event.tags.find(([n]) => n === 'p')?.[1]?.toLowerCase() || '';
  let issuedBy = event.pubkey.toLowerCase();
  let startAt = 0;
  let expiresAt = 0;
  let txid = '';
  let revoked = false;
  const issuedAt = event.created_at;

  try {
    const c = JSON.parse(event.content);
    if (typeof c.businessPubkey === 'string') businessPubkey = c.businessPubkey.toLowerCase();
    if (typeof c.issuedBy === 'string') issuedBy = c.issuedBy.toLowerCase();
    if (typeof c.startAt === 'number') startAt = c.startAt;
    if (typeof c.expiresAt === 'number') expiresAt = c.expiresAt;
    if (typeof c.txid === 'string') txid = c.txid.toLowerCase();
    if (c.revoked === true) revoked = true;
  } catch {
    revoked = true;
  }

  if (!HEX64.test(businessPubkey)) return null;
  // The d tag must match the business pubkey in the content — prevents spoofing.
  if (d.slice(BUSINESS_VERIFY_GRANT_D_PREFIX.length) !== businessPubkey) return null;

  return { businessPubkey, issuedBy, startAt, expiresAt, txid, revoked, issuedAt, event };
}

/** Reduce a list of grant events into the newest grant per business. */
export function newestGrantsByBusiness(events: NostrEvent[]): Map<string, VerificationGrant> {
  const byBiz = new Map<string, VerificationGrant>();
  for (const event of events) {
    const g = parseVerificationGrant(event);
    if (!g) continue;
    const existing = byBiz.get(g.businessPubkey);
    if (!existing || g.issuedAt > existing.issuedAt) byBiz.set(g.businessPubkey, g);
  }
  return byBiz;
}

/** Compute verification state from the newest grant + claim for a business. */
export function computeVerificationState(
  grant: VerificationGrant | null,
  claim: VerifyClaim | null,
  now = Math.floor(Date.now() / 1000),
): VerificationState {
  const base = { grant, claim, startAt: 0, expiresAt: 0, daysUntilExpiry: 0 };

  if (grant && !grant.revoked && grant.expiresAt > 0) {
    const daysUntilExpiry = Math.floor((grant.expiresAt - now) / 86400);
    if (now >= grant.startAt && now < grant.expiresAt) {
      return { ...base, status: 'VERIFIED', startAt: grant.startAt, expiresAt: grant.expiresAt, daysUntilExpiry };
    }
    if (now >= grant.expiresAt) {
      return { ...base, status: 'EXPIRED', startAt: grant.startAt, expiresAt: grant.expiresAt, daysUntilExpiry };
    }
    return { ...base, status: 'PENDING', startAt: grant.startAt, expiresAt: grant.expiresAt, daysUntilExpiry };
  }

  if (claim) {
    const revokedAfterClaim = grant?.revoked && grant.issuedAt >= claim.claimedAt;
    if (!revokedAfterClaim) return { ...base, status: 'PENDING' };
  }

  return { ...base, status: 'UNVERIFIED' };
}

/** Which grant issuers currently have an active (non-expired, non-revoked) grant. */
export function activeVerifiedPubkeys(
  events: NostrEvent[],
  now = Math.floor(Date.now() / 1000),
): Set<string> {
  const verified = new Set<string>();
  for (const grant of newestGrantsByBusiness(events).values()) {
    if (!grant.revoked && grant.startAt <= now && grant.expiresAt > now) {
      verified.add(grant.businessPubkey);
    }
  }
  return verified;
}

const CORS_PROXY = 'https://proxy.shakespeare.diy/?url=';

/** Result of auditing a payment on-chain. */
export interface TxAuditResult {
  verdict: 'paid' | 'underpaid' | 'wrong-recipient' | 'not-found' | 'no-record';
  paidSats?: number;
  expectedSats?: number;
}

/** Normalize a BCH address (strip prefix, lowercase) for comparison. */
function normalizeAddress(addr: string): string {
  return (addr || '').trim().toLowerCase().replace(/^bitcoincash:/, '');
}

/** Fetch how many sats a txid paid to the target address (via block explorers). */
async function fetchTxPayment(
  txid: string,
  feeAddress: string,
  signal?: AbortSignal,
): Promise<{ paidSats: number } | null> {
  const target = normalizeAddress(feeAddress);

  // 1) bchexplorer.cash
  try {
    const url = encodeURIComponent(`https://bchexplorer.cash/api/v1/tx/${txid}`);
    const res = await fetch(`${CORS_PROXY}${url}`, { signal: signal ?? AbortSignal.timeout(10000) });
    if (res.ok) {
      const json = await res.json();
      const outs = Array.isArray(json?.vout) ? json.vout : null;
      if (outs) {
        let paidSats = 0;
        for (const o of outs) {
          const addr = normalizeAddress(String(o?.scriptpubkey_address || o?.address || ''));
          if (addr && addr === target) paidSats += Number(o?.value ?? 0);
        }
        return { paidSats };
      }
    }
  } catch {
    // fall through
  }

  // 2) blockchair fallback
  try {
    const url = encodeURIComponent(
      `https://api.blockchair.com/bitcoin-cash/dashboards/transaction/${txid}`,
    );
    const res = await fetch(`${CORS_PROXY}${url}`, { signal: signal ?? AbortSignal.timeout(10000) });
    if (res.ok) {
      const json = await res.json();
      const data = json?.data?.[txid];
      const outs = Array.isArray(data?.outputs) ? data.outputs : null;
      if (outs) {
        let paidSats = 0;
        for (const o of outs) {
          const addr = normalizeAddress(String(o?.recipient || ''));
          if (addr && addr === target) paidSats += Number(o?.value ?? 0);
        }
        return { paidSats };
      }
    }
  } catch {
    // fall through
  }

  return null;
}

/** Tolerance for the paid amount (price moves between quote and payment). */
const AMOUNT_TOLERANCE = 0.9;

/**
 * Audit a payment: confirm the txid actually paid (close to) the expected fee
 * to the verification address. Used to give the merchant instant feedback
 * before publishing their claim.
 */
export async function auditClaim(
  txid: string,
  expectedSats: number,
  signal?: AbortSignal,
): Promise<TxAuditResult> {
  const clean = (txid || '').trim().toLowerCase();
  if (!clean || !TXID_RE.test(clean)) return { verdict: 'no-record' };

  const summary = await fetchTxPayment(clean, PLATFORM_FEE_ADDRESS, signal);
  if (!summary) return { verdict: 'not-found' };

  if (summary.paidSats <= 0) {
    return { verdict: 'wrong-recipient', paidSats: summary.paidSats, expectedSats };
  }
  if (expectedSats && summary.paidSats < expectedSats * AMOUNT_TOLERANCE) {
    return { verdict: 'underpaid', paidSats: summary.paidSats, expectedSats };
  }
  return { verdict: 'paid', paidSats: summary.paidSats, expectedSats };
}
